import React, { useEffect, useState } from 'react'
import { getTechnicians, createTechnician, updateTechnician } from '../api/client'
import { usePermissions } from '../contexts/PermissionContext'
import { Plus, UserCheck, UserX, Phone, Mail } from 'lucide-react'

const ROLES = ['admin', 'manager', 'dispatcher', 'technician']

const SKILLS = [
  'HVAC',
  'Refrigeration',
  'Ice Machine',
  'Kitchen Equipment',
  'Electrical',
  'Plumbing',
  'General Repair'
]

export default function Technicians() {

  const { hasPermission } = usePermissions()

  const canCreate = hasPermission('technicians:create')
  const canEdit = hasPermission('technicians:edit')

  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)

  const [showForm, setShowForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)

  const [editingUser, setEditingUser] = useState(null)

  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'technician',
    skills: ''
  })

  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: ''
  })

  const load = () => {
    setLoading(true)
    getTechnicians()
      .then(r => setUsers(r.data))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const roleColor = {
    admin: '#7C3AED',
    manager: '#2563EB',
    dispatcher: '#0891B2',
    technician: '#059669'
  }

  const openEditForm = (user) => {

    setEditingUser(user)

    setForm({
      email: user.email || '',
      password: '',
      full_name: user.full_name || '',
      phone: user.phone || '',
      role: user.role || 'technician',
      skills: user.skills || ''
    })

    setShowEditForm(true)
  }

  const handleSubmit = async (e) => {

    e.preventDefault()

    if (showEditForm && editingUser) {

      await updateTechnician(editingUser.id, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        skills: form.skills
      })

      setShowEditForm(false)
      setEditingUser(null)

    } else {

      await createTechnician(form)

      setShowForm(false)
    }

    setForm({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      role: 'technician',
      skills: ''
    })

    load()
  }

  const handleResetPassword = async (e) => {

    e.preventDefault()

    if (!editingUser) return

    if (!passwordForm.password || passwordForm.password !== passwordForm.confirmPassword) {
      alert('Passwords do not match')
      return
    }

    await updateTechnician(editingUser.id, {
      password: passwordForm.password
    })

    setPasswordForm({ password: '', confirmPassword: '' })
    setShowPasswordForm(false)
    setEditingUser(null)

    alert('Password updated successfully')
  }

  const toggleAvailability = async (user) => {

    await updateTechnician(user.id, {
      is_available: !user.is_available
    })

    load()
  }

  return (
    <div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Team Members</h1>

        {canCreate && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 18px',
              background: '#2563EB',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            <Plus size={16} /> Add Member
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>

          {users.map(user => (

            <div key={user.id}
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                border: `1px solid ${user.is_available ? '#D1FAE5' : '#F3F4F6'}`
              }}
            >

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>

                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: roleColor[user.role],
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700
                    }}
                  >
                    {user.full_name?.[0]}
                  </div>

                  <div>

                    <div style={{ fontWeight: 700 }}>{user.full_name}</div>

                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 20,
                        background: roleColor[user.role] + '20',
                        color: roleColor[user.role]
                      }}
                    >
                      {user.role}
                    </span>

                  </div>
                </div>

                {canEdit && (
                  <button
                    onClick={() => toggleAvailability(user)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {user.is_available
                      ? <UserCheck size={20} color="#10B981" />
                      : <UserX size={20} color="#9CA3AF" />
                    }
                  </button>
                )}
              </div>

              <div style={{ fontSize: 13, color: '#6B7280' }}>
                {user.phone && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Phone size={13} /> {user.phone}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Mail size={13} /> {user.email}
                </div>
              </div>

              {user.skills && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {user.skills.split(',').map(skill => (
                    <span
                      key={skill}
                      style={{
                        background: '#EFF6FF',
                        color: '#1D4ED8',
                        padding: '2px 8px',
                        borderRadius: 20,
                        fontSize: 11
                      }}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              )}

              {canEdit && (
                <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>

                  <button
                    onClick={() => openEditForm(user)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: '1px solid #D1D5DB',
                      background: '#fff',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => {
                      setEditingUser(user)
                      setShowPasswordForm(true)
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#F3F4F6',
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600
                    }}
                  >
                    Reset Password
                  </button>

                </div>
              )}

            </div>
          ))}

        </div>
      )}

      {/* ADD / EDIT MODAL */}

      {(showForm || showEditForm) && (

        <Modal
          title={showEditForm ? 'Edit Member' : 'Add Member'}
          onClose={() => {
            setShowForm(false)
            setShowEditForm(false)
          }}
        >

          <UserForm
            form={form}
            setForm={setForm}
            handleSubmit={handleSubmit}
            showEditForm={showEditForm}
          />

        </Modal>
      )}

      {/* PASSWORD MODAL */}

      {showPasswordForm && (

        <Modal title="Reset Password"
          onClose={() => {
            setShowPasswordForm(false)
            setPasswordForm({ password: '', confirmPassword: '' })
          }}
        >

          <form onSubmit={handleResetPassword}>

            <Input
              label="New Password"
              type="password"
              value={passwordForm.password}
              onChange={v => setPasswordForm({ ...passwordForm, password: v })}
            />

            <Input
              label="Confirm Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={v => setPasswordForm({ ...passwordForm, confirmPassword: v })}
            />

            <button
              type="submit"
              style={{
                marginTop: 10,
                width: '100%',
                padding: 10,
                background: '#2563EB',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              Update Password
            </button>

          </form>

        </Modal>
      )}

    </div>
  )
}

function Modal({ title, children, onClose }) {

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 100
    }}>

      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 28,
        width: '100%',
        maxWidth: 480
      }}>

        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{title}</h2>

        {children}

        <button
          onClick={onClose}
          style={{
            marginTop: 12,
            width: '100%',
            padding: 10,
            border: '1px solid #D1D5DB',
            borderRadius: 8,
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>

      </div>
    </div>
  )
}

function Input({ label, value, onChange, type = 'text' }) {

  return (
    <div style={{ marginBottom: 14 }}>

      <label style={{ fontSize: 12, fontWeight: 600 }}>{label}</label>

      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #D1D5DB',
          borderRadius: 7,
          fontSize: 13
        }}
      />

    </div>
  )
}

function UserForm({ form, setForm, handleSubmit, showEditForm }) {

  return (
    <form onSubmit={handleSubmit}>

      <Input label="Full Name" value={form.full_name}
        onChange={v => setForm({ ...form, full_name: v })}
      />

      <Input label="Email" value={form.email}
        onChange={v => setForm({ ...form, email: v })}
      />

      {!showEditForm && (
        <Input label="Password" type="password"
          value={form.password}
          onChange={v => setForm({ ...form, password: v })}
        />
      )}

      <Input label="Phone"
        value={form.phone}
        onChange={v => setForm({ ...form, phone: v })}
      />

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Role</label>
        <select
          value={form.role}
          onChange={e => setForm({ ...form, role: e.target.value })}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #D1D5DB',
            borderRadius: 7,
            fontSize: 13,
            background: '#fff'
          }}
        >
          {ROLES.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        style={{
          marginTop: 10,
          width: '100%',
          padding: 10,
          background: '#2563EB',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer'
        }}
      >
        {showEditForm ? 'Update Member' : 'Add Member'}
      </button>

    </form>
  )
}